import React, { useMemo } from 'react';
import { useEditor } from '../store/EditorContext';
import { 
    verticalListSortingStrategy, 
    SortableContext 
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { SortableField } from './SortableField';
import { GroupContainer } from './GroupContainer';
import { Field } from '../types';

interface CanvasProps {
    fields: Field[]; 
}

export const Canvas: React.FC<CanvasProps> = ({ fields: propFields }) => {
  const { state, dispatch } = useEditor();
  const { groups } = state.form;
  
  const { setNodeRef: setRootRef } = useDroppable({
      id: 'root-container',
  });

  const displayFields = propFields;

  const groupedFields = useMemo(() => {
      const map: Record<string, Field[]> = {};
      groups.forEach(g => map[g.id] = []);
      map['root'] = [];

      displayFields.forEach(field => {
          const key = field.groupId && map[field.groupId] ? field.groupId : 'root';
          map[key].push(field);
      });
      return map;
  }, [displayFields, groups]);

  const handleSelect = (id: string, type: 'field' | 'group') => {
      if (id.includes('-ghost')) return;
      dispatch({ type: 'SELECT_ELEMENT', payload: { id, type } });
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch({ type: 'DELETE_FIELD', payload: id });
  };

  const handleDeleteGroup = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch({ type: 'DELETE_GROUP', payload: id });
  }

  return (
    <main className="flex-1 bg-slate-50 p-8 pb-40 h-[calc(100vh-64px)] overflow-y-auto flex justify-center scrollbar-custom">
      <div className="w-full max-w-6xl animate-fade-in">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 min-h-[600px] p-8 md:p-10">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 border-b border-slate-100 pb-6 mb-8">
                {state.form.name}
            </h2>
            
            <div className="space-y-8">
                {/* Groups Area */}
                <SortableContext items={groups.map(g => g.id)} strategy={verticalListSortingStrategy}>
                    {groups.map(group => (
                        <GroupContainer
                            key={group.id}
                            group={group}
                            fields={groupedFields[group.id]}
                            isSelected={state.selectedElementId === group.id && state.selectedElementType === 'group'}
                            onSelect={() => handleSelect(group.id, 'group')}
                            onDelete={(e) => handleDeleteGroup(group.id, e)}
                        />
                    ))}
                </SortableContext>

                {/* Ungrouped Fields Area (Root) */}
                <SortableContext id="root-context" items={groupedFields['root'].map(f => f.id)} strategy={verticalListSortingStrategy}>
                        <div 
                            ref={setRootRef}
                            id="root-container"
                            className="bg-slate-50/50 rounded-xl p-6 border border-dashed border-slate-300 min-h-[150px] transition-colors hover:bg-slate-50 hover:border-teal-300"
                        >
                        {groupedFields['root'].length === 0 && (
                            <div className="text-center text-slate-400 text-sm font-medium italic mb-2 py-10 pointer-events-none">
                                Drop fields here to start
                            </div>
                        )}
                        <div className="space-y-4">
                            {groupedFields['root'].map(field => (
                                <SortableField 
                                    key={field.id} 
                                    field={field} 
                                    isSelected={state.selectedElementId === field.id && state.selectedElementType === 'field'}
                                    onSelect={() => handleSelect(field.id, 'field')}
                                    onDelete={(e) => handleDelete(field.id, e)}
                                />
                            ))}
                        </div>
                        </div>
                </SortableContext>
            </div>
        </div>
      </div>
    </main>
  );
};